import { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import api from "../../api/axios";

import "../../assets/css/Deals/DealsCreate.css";
import { validateExpenseAgainstDeal } from "../../utils/expenseValidation";

const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xlsx",
  ".xls",
  ".png",
  ".jpg",
  ".jpeg",
];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

export default function DealsCreate({
  isOpen,
  onClose,
  lead,
  onSuccess,
  pendingFollowup = null,
  isConversionFlow = false,
  requireDocument,
}) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [expenseError, setExpenseError] = useState("");
  const [docError, setDocError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [createdDeal, setCreatedDeal] = useState(null);
  const fileInputRef = useRef(null);

  const isDocRequired =
    requireDocument !== undefined
      ? requireDocument
      : Boolean(isConversionFlow || pendingFollowup || (lead && lead.requireDoc));

  const [form, setForm] = useState({
    lead_id: "",
    lead_amount: "",
    deal_amount: "",
    expiry_date: "",
    po_document: null,
    po_number: "",
    status: "Active Deal",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes] = await Promise.all([api.get("/users")]);
        setUsers(userRes.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (lead) {
      setForm({
        lead_id: lead.id,
        lead_amount: lead.value || "",
        deal_amount: "",
        expected_expense: "",
        expiry_date: "",
        po_document: null,
        po_number: "",
        status: "Active Deal",
      });
      setCreatedDeal(null);
      setDocError("");
      setExpenseError("");
    }
  }, [lead]);

  if (!isOpen) return null;

  const validateDocument = (file, isRequired) => {
    if (!file) {
      if (isRequired) {
        return {
          isValid: false,
          error: "PO Document is required to create a deal.",
        };
      }
      return { isValid: true, error: "" };
    }

    const filename = file.name || "";
    const lastDot = filename.lastIndexOf(".");
    if (lastDot === -1) {
      return {
        isValid: false,
        error:
          "Invalid file type. Allowed formats: PDF, DOC, DOCX, XLSX, XLS, PNG, JPG, JPEG.",
      };
    }

    const ext = filename.substring(lastDot).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return {
        isValid: false,
        error: `Invalid file type '${ext}'. Allowed formats: PDF, DOC, DOCX, XLSX, XLS, PNG, JPG, JPEG.`,
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: "File exceeds maximum allowed size of 25MB.",
      };
    }

    return { isValid: true, error: "" };
  };

  const handleFileSelected = (file) => {
    setDocError("");
    if (!file) {
      setForm((prev) => ({ ...prev, po_document: null }));
      return;
    }

    const validation = validateDocument(file, isDocRequired);
    if (!validation.isValid) {
      setDocError(validation.error);
      setForm((prev) => ({ ...prev, po_document: null }));
      Swal.fire({
        icon: "error",
        title: "Unsupported File Format",
        text: validation.error,
      });
      return;
    }

    setForm((prev) => ({
      ...prev,
      po_document: file,
    }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelected(files[0]);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    // FILE INPUT
    if (files) {
      handleFileSelected(files[0]);
      return;
    }

    // NUMBER FORMATTED FIELDS
    if (name === "deal_amount" || name === "expected_expense") {
      setExpenseError("");
      const rawValue = value.replace(/,/g, "").replace(/\D/g, "");

      setForm((prev) => ({
        ...prev,
        [name]: rawValue,
      }));

      return;
    }

    // DEFAULT FIELDS
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    if (loading) return;
    setDocError("");
    setExpenseError("");
    setCreatedDeal(null);
    onClose?.();
  };

  // ✅ API CALL
  const submit = async (e) => {
    e.preventDefault();

    const validation = validateExpenseAgainstDeal(
      form.expected_expense,
      form.deal_amount
    );

    if (!validation.isValid) {
      setExpenseError(validation.error);
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: validation.error,
      });
      return;
    }

    // Document Validation
    if (isDocRequired && !form.po_document) {
      setDocError("PO Document is required to create a deal.");
      Swal.fire({
        icon: "warning",
        title: "Document Required",
        text: "PO Document is required to create a deal.",
      });
      return;
    }

    if (form.po_document) {
      const docValidation = validateDocument(form.po_document, isDocRequired);
      if (!docValidation.isValid) {
        setDocError(docValidation.error);
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: docValidation.error,
        });
        return;
      }
    }

    try {
      setLoading(true);

      const payload = {
        title: lead?.title || "Deal",
        organization_id: lead?.organization?.id || 0,
        lead_id: form.lead_id || (lead?.id ?? null),
        deal_amount: Number(form.deal_amount),
        contact_id: lead?.contact?.id || 0,
        expected_expense: Number(form.expected_expense),
        expiry_date: form.expiry_date,
        status: form.status,
        profit: Number(form.deal_amount) - Number(form.expected_expense),
        actual_profit: Number(form.deal_amount) - Number(form.expected_expense),
        assigned_to: form.assign_to,
        expected_close_date: form.expiry_date,
      };

      // 1️⃣ Create Deal (or reuse deal created in previous attempt to prevent duplicates)
      let dealData = createdDeal;
      if (!dealData?.id) {
        const dealRes = await api.post("/deal/create", payload, {});
        dealData = dealRes?.data;
        setCreatedDeal(dealData);
      }

      // 2️⃣ Upload PO Document
      if (form.po_document && dealData?.id) {
        try {
          const docFormData = new FormData();
          docFormData.append("file", form.po_document);
          docFormData.append("doc_type", "PO");
          docFormData.append("doc_number", form.po_number || "");
          docFormData.append("amount", form.deal_amount || "");
          docFormData.append("stage", form.status || "Active Deal");
          docFormData.append("notes", "Attached upon deal creation");

          await api.post(`/deal/${dealData.id}/documents/upload`, docFormData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uploadErr) {
          console.error("PO document upload error:", uploadErr);
          const uploadMsg =
            uploadErr?.response?.data?.detail ||
            "Failed to upload PO document. Please check the file and try again.";
          Swal.fire({
            icon: "error",
            title: "Document Upload Failed",
            text: uploadMsg,
          });
          setLoading(false);
          // Return without creating follow-up or changing lead status!
          return;
        }
      }

      // 3️⃣ Complete Pending Follow-Up if present
      if (pendingFollowup) {
        try {
          await api.post("/lead-followup/create", pendingFollowup);
        } catch (followupErr) {
          console.warn("Follow-up submission warning:", followupErr);
        }
      }

      // 4️⃣ Update Lead Status
      if (lead?.id) {
        await api.put(`/lead/update/${lead.id}`, {
          status: "Deal Confirmation",
          title: lead.title,
        });
      }

      Swal.fire({
        icon: "success",
        title: "Deal Created",
        timer: 1500,
        showConfirmButton: false,
      });

      await onSuccess?.(dealData);
      await onClose?.();
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Failed to create deal",
        text: err?.response?.data?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Create Deal</h3>
          <button
            type="button"
            className="close-btn"
            onClick={handleCancel}
            disabled={loading}
            aria-label="close modal"
          >
            ✕
          </button>
        </div>

        <form className="modal-body" onSubmit={submit}>
          <div className="form-grid">
            {/* LEAD INFO (READ ONLY) */}
            <div className="form-group">
              <label>Lead Title</label>
              <input
                value={lead?.title || ""}
                disabled
                placeholder="Website Development Lead"
              />
            </div>

            <div className="form-group">
              <label>Organization</label>
              <input
                value={lead?.organization?.name || ""}
                disabled
                placeholder="ABC Pvt Ltd"
              />
            </div>

            {/* LEAD AMOUNT */}
            <div className="form-group">
              <label>Expected Revenue</label>
              <input
                type="text"
                name="lead_amount"
                readOnly
                value={
                  form.lead_amount
                    ? Number(form.lead_amount).toLocaleString("en-IN")
                    : ""
                }
                onChange={handleChange}
                placeholder="50,000"
                required
              />
            </div>

            {/* DEAL AMOUNT */}
            <div className="form-group">
              <label>Actual Revenue</label>
              <input
                type="text"
                name="deal_amount"
                value={
                  form.deal_amount
                    ? Number(form.deal_amount).toLocaleString("en-IN")
                    : ""
                }
                onChange={handleChange}
                placeholder="1,25,000"
                required
              />
            </div>

            {/* EXPECTED EXPENSE */}
            <div className="form-group">
              <label>Expected Expense</label>
              <input
                type="text"
                name="expected_expense"
                value={
                  form.expected_expense
                    ? Number(form.expected_expense).toLocaleString("en-IN")
                    : ""
                }
                onChange={handleChange}
                placeholder="20,000"
                required
              />
              {expenseError && (
                <span
                  className="error-text"
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {expenseError}
                </span>
              )}
            </div>

            {/* ASSIGN TO */}
            <div className="form-group">
              <label>Assign To</label>
              <select
                name="assign_to"
                value={form.assign_to}
                onChange={handleChange}
                required
              >
                <option value="">Select User</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>

            {/* EXPIRY DATE */}
            <div className="form-group">
              <label>Expected Close Date</label>
              <input
                type="date"
                name="expiry_date"
                value={form.expiry_date}
                onChange={handleChange}
                required
              />
            </div>

            {/* PO NUMBER */}
            <div className="form-group">
              <label htmlFor="po_number">PO Number (Optional)</label>
              <input
                id="po_number"
                type="text"
                name="po_number"
                value={form.po_number || ""}
                onChange={handleChange}
                placeholder="e.g. PO-2026-001"
              />
            </div>

            {/* PO DOCUMENT UPLOAD & DRAG-AND-DROP AREA */}
            <div className="form-group" style={{ gridColumn: "span 2" }}>
              <label>
                Attach PO Document{" "}
                {isDocRequired ? (
                  <span style={{ color: "#ef4444" }}>*</span>
                ) : (
                  "(Optional)"
                )}
              </label>

              {/* Standard hidden/visible input for test and DOM accessibility */}
              <input
                ref={fileInputRef}
                type="file"
                name="po_document"
                style={{ display: "none" }}
                onChange={handleChange}
                accept=".pdf,.doc,.docx,.xlsx,.xls,.png,.jpg,.jpeg"
              />

              {!form.po_document ? (
                <div
                  className={`drop-area ${isDragging ? "active" : ""}`}
                  data-testid="po-drop-area"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  aria-label="Upload PO document"
                  style={{
                    border: isDragging
                      ? "2px dashed #0d9488"
                      : "2px dashed #cbd5e1",
                    borderRadius: "8px",
                    padding: "20px",
                    textAlign: "center",
                    background: isDragging ? "#f0fdfa" : "#f8fafc",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      fontSize: "28px",
                      color: "#64748b",
                      marginBottom: "6px",
                    }}
                  >
                    📄
                  </div>
                  <p
                    style={{
                      margin: "0 0 4px",
                      fontWeight: 600,
                      color: "#1e293b",
                      fontSize: "14px",
                    }}
                  >
                    Click to upload or drag &amp; drop PO document
                  </p>
                  <p style={{ margin: 0, fontSize: "12px", color: "#64748b" }}>
                    Allowed: PDF, DOC, DOCX, XLSX, XLS, PNG, JPG, JPEG (Max
                    25MB)
                  </p>
                </div>
              ) : (
                <div
                  className="selected-file-card"
                  data-testid="selected-po-file"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      minWidth: 0,
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>📄</span>
                    <div style={{ minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#1e293b",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={form.po_document.name}
                      >
                        {form.po_document.name}
                      </p>
                      <span style={{ fontSize: "11px", color: "#64748b" }}>
                        {form.po_document.size < 1024 * 1024
                          ? `${(form.po_document.size / 1024).toFixed(1)} KB`
                          : `${(
                              form.po_document.size /
                              (1024 * 1024)
                            ).toFixed(2)} MB`}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", gap: "8px", flexShrink: 0 }}
                  >
                    <button
                      type="button"
                      className="btn-replace-file"
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        padding: "5px 12px",
                        fontSize: "12px",
                        background: "#ffffff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="btn-remove-file"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, po_document: null }));
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }}
                      style={{
                        padding: "5px 10px",
                        fontSize: "12px",
                        background: "#fee2e2",
                        color: "#dc2626",
                        border: "1px solid #fecaca",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                      aria-label="Remove document"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {docError && (
                <span
                  className="error-text"
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    marginTop: "4px",
                    display: "block",
                  }}
                >
                  {docError}
                </span>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading
                ? createdDeal
                  ? "Uploading..."
                  : "Creating..."
                : createdDeal
                ? "Retry Upload"
                : "Create Deal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
